import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

// =============================================================================
// API Client Configuration
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
})

// =============================================================================
// Token Refresh State
// =============================================================================

let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token)
    } else {
      reject(error)
    }
  })
  failedQueue = []
}

function forceLogout() {
  sessionStorage.removeItem('access_token')
  sessionStorage.removeItem('user')
  // Older builds wrote the user object to localStorage; wipe any residue
  localStorage.removeItem('user')
  window.location.href = '/login'
}

// =============================================================================
// Request Interceptor - Add auth token
// =============================================================================

api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// =============================================================================
// Response Interceptor - Auto-refresh expired tokens before logging out
// =============================================================================

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Only handle 401 errors (not network errors, timeouts, etc.)
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    // Don't retry the refresh endpoint itself or login — that would loop
    const url = originalRequest.url || ''
    if (url.includes('/api/auth/refresh') || url.includes('/api/auth/login')) {
      forceLogout()
      return Promise.reject(error)
    }

    // Don't retry if we already retried this request
    if (originalRequest._retry) {
      forceLogout()
      return Promise.reject(error)
    }

    // If another request is already refreshing, queue this one
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`
        return api(originalRequest)
      }).catch((err) => {
        return Promise.reject(err)
      })
    }

    // Attempt token refresh
    originalRequest._retry = true
    isRefreshing = true

    try {
      const token = sessionStorage.getItem('access_token')
      if (!token) {
        throw new Error('No token to refresh')
      }

      // Call refresh endpoint with the expired token
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refresh`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        }
      )

      const newToken = response.data.access_token
      sessionStorage.setItem('access_token', newToken)

      // Process any queued requests with the new token
      processQueue(null, newToken)

      // Retry the original request with the new token
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return api(originalRequest)
    } catch (refreshError) {
      // Refresh failed — token is too old or user is deactivated
      processQueue(refreshError, null)
      forceLogout()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

// =============================================================================
// API Error Type
// =============================================================================

export interface ApiError {
  message: string
  detail?: string
  status?: number
}

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (data) {
      // Pydantic 422 validation errors: detail is an array of objects
      if (Array.isArray(data.detail)) {
        const messages = data.detail.map(
          (e: { msg?: string; loc?: string[] }) => e.msg || 'Validation error'
        )
        return messages.join('. ')
      }
      // Standard FastAPI errors: detail is a string
      if (typeof data.detail === 'string') {
        return data.detail
      }
      if (typeof data.message === 'string') {
        return data.message
      }
    }
    return error.message || 'An unexpected error occurred'
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unexpected error occurred'
}

// =============================================================================
// Generic API Methods
// =============================================================================

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.get<T>(url, config)
  return response.data
}

export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.post<T>(url, data, config)
  return response.data
}

export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.put<T>(url, data, config)
  return response.data
}

export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.patch<T>(url, data, config)
  return response.data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.delete<T>(url, config)
  return response.data
}

// =============================================================================
// File Download
// =============================================================================

export async function downloadFile(url: string, filename: string): Promise<void> {
  const response = await api.get(url, {
    responseType: 'blob',
    timeout: 120000, // 2 minutes for large report downloads
  })

  // Create blob link to download
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = blobUrl
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(blobUrl)
}

export default api
